import { useState, useEffect } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { CreateTaskDialog } from "./CreateTaskDialog";

export function CreateTaskFromSubmissionDialog({ 
    open, 
    onOpenChange, 
    submission, 
    formSlug,
    onTaskCreated 
}) {
    const { toast } = useToast();
    const { axiosInstance } = useAuth();
    
    const [projects, setProjects] = useState([]);
    const [loadingProjects, setLoadingProjects] = useState(false);
    const [loadingAI, setLoadingAI] = useState(true);
    const [taskSuggestion, setTaskSuggestion] = useState(null);

    // Fetch projects and generate task suggestion when dialog opens
    useEffect(() => {
        if (open && formSlug) {
            fetchProjectsAndGenerateTask();
        }
    }, [open, formSlug]);

    const fetchProjectsAndGenerateTask = async () => {
        setLoadingProjects(true);
        try {
            // Fetch projects from the form's organization
            const projectsResponse = await axiosInstance.get(`/forms/${formSlug}/projects`);
            setProjects(projectsResponse.data);
            setLoadingProjects(false); // Projects loaded, now start AI

            // Generate AI task suggestion if we have submission data
            if (submission) {
                await generateAITaskSuggestion();
            }
        } catch (error) {
            console.error('Error fetching projects:', error);
            toast({
                title: "Error",
                description: "Failed to load projects",
                variant: "destructive"
            });
            setLoadingProjects(false);
        }
    };

    const generateAITaskSuggestion = async () => {
        setLoadingAI(true);
        try {
            // Call the AI endpoint to generate task suggestion
            const response = await axiosInstance.post(
                `/forms/submissions/${submission.id}/generate-task`,
                {
                    submissionData: submission.data
                }
            );

            if (response.data.success) {
                // Extract files from submission data to show as preloaded attachments
                const submissionFiles = [];
                if (submission.data) {
                    Object.values(submission.data).forEach(value => {
                        if (Array.isArray(value)) {
                            // Check if this looks like file data
                            value.forEach(item => {
                                if (item && typeof item === 'object' && item.filename && item.key) {
                                    submissionFiles.push({
                                        id: item.key,
                                        name: item.filename,
                                        size: item.size || 0,
                                        type: item.contentType || 'application/octet-stream',
                                        file: null, // No actual File object since it's already uploaded
                                        alt: item.filename,
                                        url: null, // Will be generated when needed
                                        key: item.key,
                                        filename: item.filename,
                                        contentType: item.contentType,
                                        fromSubmission: true // Flag to indicate this came from submission
                                    });
                                }
                            });
                        }
                    });
                }

                setTaskSuggestion({
                    summary: response.data.data.summary,
                    description: response.data.data.description,
                    submissionFiles: submissionFiles // Include files in the task suggestion
                });
            } else {
                toast({
                    description: "Failed to generate task suggestion",
                    variant: "destructive"
                });
            }
        } catch (error) {
            console.error('Error generating AI task suggestion:', error);
            toast({
                description: "Failed to generate task suggestion",
                variant: "destructive"
            });
        } finally {
            setLoadingAI(false);
        }
    };

    const handleTaskCreated = async (task) => {
        // Task creation now includes submission info, so no need to link separately
        onOpenChange(false);
        
        if (onTaskCreated) {
            onTaskCreated({
                ...task,
                linkedToSubmission: true
            });
        }
    };

    const handleClose = () => {
        onOpenChange(false);
        setTaskSuggestion(null);
    };

    // Don't render if no projects are available (and not loading)
    if (!loadingProjects && (!projects || projects.length === 0)) {
        return null;
    }

    // Prepare submission data to pass to CreateTaskDialog
    const submissionData = submission ? {
        submissionId: submission.id,
        createdAt: submission.createdAt
    } : null;

    return (
        <CreateTaskDialog
            open={open}
            onOpenChange={handleClose}
            project={projects[0]} // Default to first project
            projects={projects}
            initialData={taskSuggestion}
            onTaskCreated={handleTaskCreated}
            generatingTask={loadingAI}
            createdFromSubmission={submissionData}
        />
    );
} 